import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AlertCircle } from 'lucide-react';
import { apiClient } from '../utils/api';
import { useToast } from '../components/Toast';
import { RootState } from '../store';
import { AppointmentConsultationView } from '../components/appointment/AppointmentConsultationView';
import type {
  AppointmentConsultationData,
  ConsentByActId,
  ConsultationAct,
} from '../components/appointment/appointmentConsultationTypes';
import { normalizeAppointmentConsultationData } from '../components/appointment/appointmentConsultationUtils';

function pickConsentContract(contracts: any[] | undefined): any | null {
  if (!Array.isArray(contracts) || contracts.length === 0) return null;
  const piercing = contracts.find((c) => c?.type === 'PIERCING_CONSENT');
  return piercing ?? contracts[0] ?? null;
}

export function PractitionerAppointmentViewPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { showToast, ToastComponent } = useToast();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [appointment, setAppointment] = useState<AppointmentConsultationData | null>(null);
  const [acts, setActs] = useState<ConsultationAct[]>([]);
  const [actsLoading, setActsLoading] = useState(false);
  const [docs, setDocs] = useState<any[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [consentByActId, setConsentByActId] = useState<ConsentByActId>({});

  const authUser = useSelector((state: RootState) => state.auth.user);
  const signerName = authUser
    ? (
        `${(authUser as any).firstName || ''} ${(authUser as any).lastName || ''}`.trim() ||
        (authUser as any).name ||
        ''
      )
    : '';

  const loadDocs = useCallback(async (appointmentId: string) => {
    try {
      setDocsLoading(true);
      const resp = await apiClient(`/api/appointments/${appointmentId}/documents`);
      if (!resp.ok) return;
      const data = await resp.json();
      if (data.success) setDocs(data.data || []);
    } catch {
      // silently ignore
    } finally {
      setDocsLoading(false);
    }
  }, []);

  const loadActsAndConsent = useCallback(async (appointmentId: string) => {
    try {
      setActsLoading(true);
      const resp = await apiClient(`/api/practitioner/appointments/${appointmentId}/details`);
      if (!resp.ok) {
        setActs([]);
        setConsentByActId({});
        return;
      }
      const data = await resp.json();
      if (!data.success) {
        setActs([]);
        setConsentByActId({});
        return;
      }

      const rawActs: any[] = data.data?.acts || [];
      const sorted = [...rawActs].sort(
        (a, b) =>
          (a.sequence ?? Number.MAX_SAFE_INTEGER) - (b.sequence ?? Number.MAX_SAFE_INTEGER),
      );

      const mapped: ConsultationAct[] = sorted.map((act) => ({
        id: act.id,
        sequence: act.sequence,
        status: act.status,
        serviceId: act.serviceId ?? act.service?.id,
        serviceName: act.service?.name,
        notes: act.notes,
        performedAt: act.performedAt,
        demandDetails: act.demandDetails,
        piercingRecord: act.piercingRecord,
      }));
      setActs(mapped);

      const next: ConsentByActId = {};
      await Promise.all(
        mapped.map(async (act, index) => {
          const fromDetails = pickConsentContract(sorted[index]?.contracts);
          if (fromDetails?.type === 'PIERCING_CONSENT' || fromDetails) {
            // Prefer full contract payload (id cards / signatures) from dedicated endpoint
            try {
              const contractResp = await apiClient(`/api/practitioner/acts/${act.id}/contract`);
              if (contractResp.ok) {
                const contractData = await contractResp.json();
                if (contractData.success && contractData.data) {
                  const c = contractData.data;
                  if (c.type === 'PIERCING_CONSENT' || !c.type) {
                    next[act.id] = c;
                    return;
                  }
                }
              }
            } catch {
              /* fall through to details contract */
            }
            if (fromDetails.type === 'PIERCING_CONSENT' || fromDetails.type == null) {
              next[act.id] = fromDetails;
            }
          }
        }),
      );
      setConsentByActId(next);
    } catch {
      setActs([]);
      setConsentByActId({});
    } finally {
      setActsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const resp = await apiClient(`/api/practitioner/appointments/${id}`);
        const data = await resp.json();
        if (!resp.ok || !data?.success) {
          throw new Error(data?.message || 'Failed to load appointment');
        }
        const loaded = normalizeAppointmentConsultationData(data.data.appointment);
        setAppointment(loaded);
        if (loaded?.id && id.length === 4 && loaded.id !== id) {
          navigate(`/practitioner/appointments/${loaded.id}`, { replace: true });
          return;
        }
        if (loaded?.id) {
          void loadActsAndConsent(loaded.id);
        }
      } catch (e: any) {
        setError(e?.message || 'Failed to load appointment');
      } finally {
        setLoading(false);
      }
    })();
    loadDocs(id);
  }, [id, navigate, loadActsAndConsent, loadDocs]);

  const openConsentPdf = useCallback(
    async (actId: string) => {
      try {
        const res = await apiClient(`/api/practitioner/acts/${actId}/contract-preview-pdf`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.message || 'PDF indisponible');
        }
        return await res.blob();
      } catch (e: any) {
        showToast(e?.message || 'Impossible d’ouvrir le PDF', 'error', 5000);
        return null;
      }
    },
    [showToast],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-2 text-gray-600">Chargement...</span>
      </div>
    );
  }

  if (error || !appointment) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">{error || 'Not found'}</span>
          </div>
        </div>
        <button
          onClick={() => navigate('/practitioner/appointments')}
          className="mt-4 btn-secondary"
        >
          Retour
        </button>
      </div>
    );
  }

  return (
    <AppointmentConsultationView
      appointment={appointment}
      acts={acts}
      actsLoading={actsLoading}
      consentByActId={consentByActId}
      docs={docs}
      docsLoading={docsLoading}
      listPath="/practitioner/appointments"
      showHelpIcon={false}
      allowDocumentSign
      allowDocumentView
      onOpenConsentPdf={openConsentPdf}
      signerName={signerName}
      onReloadDocs={() => {
        if (id) void loadDocs(id);
      }}
      ToastComponent={<ToastComponent />}
    />
  );
}

export default PractitionerAppointmentViewPage;
